/**
 * Phaser-free geometry helpers used by `PaintedScene`. Kept in their own
 * module so the unit tests can import them without dragging Phaser into the
 * Vitest environment (Phaser's bundle touches `window` at import time).
 */
import type {
  InteractionHotspot,
  NormalisedPoint,
  NormalisedPolygon,
  NormalisedRect,
} from './journeyManifest.schema';

/** Default test: did `(nx, ny)` fall inside `zone`'s rect or polygon? */
export function zoneContains(
  zone: { rect?: NormalisedRect; polygon?: NormalisedPolygon },
  nx: number,
  ny: number,
): boolean {
  if (zone.rect) {
    return rectContains(zone.rect, nx, ny);
  }
  if (zone.polygon) {
    return polygonContains(zone.polygon, nx, ny);
  }
  return false;
}

export function interactionContains(spec: InteractionHotspot, nx: number, ny: number): boolean {
  return zoneContains(spec, nx, ny);
}

export function interactionCentre(spec: InteractionHotspot): NormalisedPoint {
  if (spec.anchor) return spec.anchor;
  if (spec.rect) {
    return { x: spec.rect.x + spec.rect.w / 2, y: spec.rect.y + spec.rect.h / 2 };
  }
  if (spec.polygon) {
    let sx = 0;
    let sy = 0;
    for (const p of spec.polygon) {
      sx += p.x;
      sy += p.y;
    }
    return { x: sx / spec.polygon.length, y: sy / spec.polygon.length };
  }
  return { x: 0.5, y: 0.5 };
}

function rectContains(rect: NormalisedRect, nx: number, ny: number): boolean {
  return nx >= rect.x && nx <= rect.x + rect.w && ny >= rect.y && ny <= rect.y + rect.h;
}

function polygonContains(points: ReadonlyArray<NormalisedPoint>, nx: number, ny: number): boolean {
  let inside = false;
  const n = points.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;
    const intersects = (yi > ny) !== (yj > ny)
      && nx < ((xj - xi) * (ny - yi)) / (yj - yi + 1e-12) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}
